import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { PropTypes } from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { COLORS } from '../../../constants/colors';
import { usePodcastLogo } from '../../../graphql/hooks/use-podcast-logo';
import { toCamelCase } from '../../../utils';
import PlayButton from '../../Global/PlayButton';
import Spinner from '../../Global/Spinner';
import Volume from '../../Global/Volume';
import PodcastsLink from '../../PodcastsLink';

const moment = require('moment');
require('moment-duration-format');

const ArticlePodcast = ({ color, podcast }) => {
  const getColor = COLORS[toCamelCase(color)];
  const audioRef = useRef(null);
  const prevVolumeLevel = useRef(0.5);
  const [hasLoaded, setLoaded] = useState(false);
  const [isPlaying, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMuted, setMuted] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0.5);
  const logo = usePodcastLogo();

  const togglePlayButton = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setPlaying(!isPlaying);
  };

  const handleLoaded = () => {
    // set default volume to 0.5;
    audioRef.current.volume = 0.5;
    setLoaded(true);
  };
  const handleTimeUpdate = (e) => {
    setCurrentTime(e.target.currentTime);
  };
  const handleScrubBarClick = (e) => {
    if (!hasLoaded) {
      return;
    }
    const getTime =
      audioRef.current.duration *
      (Math.max(0, e.nativeEvent.offsetX) / e.target.clientWidth);
    audioRef.current.currentTime = getTime;
    setCurrentTime(getTime);
  };
  const toggleVolume = () => {
    if (isMuted) {
      audioRef.current.muted = false;
      setVolumeLevel(prevVolumeLevel.current);
    } else {
      prevVolumeLevel.current = volumeLevel;
      setVolumeLevel(0);
      audioRef.current.muted = true;
    }
    setMuted(!isMuted);
  };
  const handleVolumeScrubBarClick = (e) => {
    const getVolume = (
      1 -
      Math.max(0, e.nativeEvent.offsetY) / e.target.clientHeight
    ).toFixed(1);
    audioRef.current.volume = getVolume;
    audioRef.current.muted = false;
    setVolumeLevel(getVolume);
    setMuted(false);
  };

  useEffect(() => {
    audioRef.current.addEventListener('timeupdate', (e) => handleTimeUpdate(e));
    return () =>
      audioRef.current &&
      audioRef.current.removeEventListener('timeupdate', (e) =>
        handleTimeUpdate(e),
      );
  }, [hasLoaded]);

  useEffect(() => {
    if (audioRef.current.duration) {
      setLoaded(true);
    }
  }, []);

  let format = 'mm:ss';
  if (hasLoaded && audioRef.current.duration >= 3600) {
    format = 'hh:mm:ss';
  }
  const clock =
    hasLoaded &&
    `${moment
      .duration(currentTime, 'seconds')
      .format(format, { trim: false })} / ${moment
      .duration(audioRef.current.duration, 'seconds')
      .format(format, { trim: false })}`;

  return (
    <div className="article__podcast">
      <div className="article__podcast--container">
        <h2 className="header-mobile">{podcast.title}</h2>
        <div className="player">
          {/* TODO: Need to include a track tag but it has to be in WebVTT format */}
          <audio ref={audioRef} onCanPlay={() => handleLoaded()}>
            <source src={podcast.audio.file.url} type="audio/mpeg" />
          </audio>
          <div
            className="play-button"
            onClick={() => togglePlayButton()}
            type="button"
          >
            <PlayButton background={getColor} isPlaying={isPlaying} />
          </div>
          <div className="progress">
            <div className="progress-header">
              <h2 className="header">{podcast.title}</h2>
              <p className="time xs">
                {hasLoaded ? clock : <Spinner inverse />}
              </p>
            </div>

            <div className="bar" onClick={(e) => handleScrubBarClick(e)}>
              <div className="inactive" />
              <div
                className="active"
                style={{
                  background: getColor,
                  width: `${
                    hasLoaded
                      ? (100 * currentTime) / audioRef.current.duration
                      : 0
                  }%`,
                }}
              />
            </div>
          </div>
          <div className="volume">
            <div
              className={`button ${isMuted ? 'muted' : ''}`}
              onClick={() => toggleVolume()}
            >
              <Volume />
            </div>
            <div className="controls">
              <div
                className="bar"
                onClick={(e) => {
                  handleVolumeScrubBarClick(e);
                }}
              >
                <div className="inactive" />
                <div
                  className="active"
                  style={{
                    background: getColor,
                    height: `${100 * volumeLevel}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="social">
            <p className="sm title">Listen on</p>
            <div className="links">
              <PodcastsLink podcasts={podcast.podcastLinks} useTitle={false} />
            </div>
          </div>
          <div className="cover">
            <GatsbyImage
              alt={podcast.podcastTheme}
              image={getImage(logo[toCamelCase(podcast.podcastTheme)])}
            />
            <img
              alt={podcast.podcastTheme}
              src={logo[toCamelCase(podcast.podcastTheme)].publicURL}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

ArticlePodcast.propTypes = {
  color: PropTypes.string,
  podcast: PropTypes.object,
};

export default ArticlePodcast;
